import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks';
import { createCandidate, createCatalogCheckoutSession, deleteCandidate, DEMO_CALL_LIMIT, findCandidatesByLinkedinUrls, getBillingPlans, getCandidate, getCandidatesStats, getDemoCallsCount, startDemoCall, startDemoScraping } from '../../lib/api';
import { mergeTrialWithStripePrice } from '../../lib/utils/billingPlans';
import { toCandidateDbPayload } from '../../lib/utils/csvParser';
import { planIsTopTier } from '../../lib/utils/planEntitlements';
import { getCandidateLimitTooltip } from '../candidates-list/components/AddCandidateButton';
import {
  DEMO_COMPLETE_VIEW_ID,
  DEMO_STEPS,
  DEMO_TEST_CALL_SKIPPED_FLAG,
  getCurrentDemoStepIndex,
  getDemoStepIndexById,
  hasSkippedDemoTestCall,
  isDemoCompleteView,
  isDemoIncomplete,
  isDemoStepId,
  normalizeDemoStepsCompleted,
} from '../../lib/demo/demoSteps';
import { waitForDemoScrape } from '../../lib/demo/waitForDemoScrape';
import { getDefaultAuthenticatedPath, needsSubscriptionSelection } from '../../lib/utils/subscriptionAccess';
import Icon from '../../components/AppIcon';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tooltip from '../../components/ui/Tooltip';
import DemoCandidateProfile from './components/DemoCandidateProfile';
import DemoComplete from './components/DemoComplete';
import DemoScrapeOverlay from './components/DemoScrapeOverlay';
import DemoStepPreview from './components/DemoStepPreview';
import DemoWelcomeVideo from './components/DemoWelcomeVideo';
import DemoTestCallModal from './components/DemoTestCallModal';
import DemoTestScrapeForm from './components/DemoTestScrapeForm';

const hasCandidatePhone = (phone) => {
  const value = String(phone || '').trim();
  return Boolean(value && value !== '+');
};

const TEST_SCRAPE_FORM_ID = 'demo-test-scrape-form';

const DemoPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const completedSteps = useMemo(
    () => normalizeDemoStepsCompleted(profile?.demo_steps_completed),
    [profile?.demo_steps_completed],
  );
  const completedSet = useMemo(() => new Set(completedSteps), [completedSteps]);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completeAction, setCompleteAction] = useState(null);
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [demoCandidateId, setDemoCandidateId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('candidate') || '';
  });
  const [scrapeFormValues, setScrapeFormValues] = useState(null);
  const [hasDemoPhone, setHasDemoPhone] = useState(false);
  const [isDemoUnknown, setIsDemoUnknown] = useState(false);
  const [demoCandidateName, setDemoCandidateName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTestCallModal, setShowTestCallModal] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [isStartingCall, setIsStartingCall] = useState(false);
  const [callRequested, setCallRequested] = useState(false);
  const [callError, setCallError] = useState('');
  const [demoCallCount, setDemoCallCount] = useState(0);
  const [showCallWaiting, setShowCallWaiting] = useState(false);
  const [showComplete, setShowComplete] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isDemoCompleteView(new URLSearchParams(window.location.search).get('step'));
  });

  useEffect(() => {
    if (!profile || initialized) return;

    const fromQuery = searchParams.get('step');
    const skippedCall = hasSkippedDemoTestCall(profile.demo_steps_completed);

    if (isDemoStepId(fromQuery)) {
      setShowComplete(false);
      setStepIndex(getDemoStepIndexById(fromQuery));
    } else if (profile.demo_completed_at) {
      setShowComplete(false);
      setStepIndex(skippedCall ? getDemoStepIndexById('test-call') : 0);
    } else if (isDemoCompleteView(fromQuery) || skippedCall) {
      setShowComplete(true);
      setStepIndex(getDemoStepIndexById('candidate-profile'));
    } else {
      setShowComplete(false);
      setStepIndex(getCurrentDemoStepIndex(profile));
    }
    setInitialized(true);
  }, [profile, initialized, searchParams]);

  useEffect(() => {
    if (!initialized) return;

    if (showComplete) {
      if (searchParams.get('step') === DEMO_COMPLETE_VIEW_ID) return;
      setSearchParams({ step: DEMO_COMPLETE_VIEW_ID }, { replace: true });
      return;
    }

    const stepId = DEMO_STEPS[stepIndex]?.id;
    if (!stepId) return;

    const nextParams = { step: stepId };
    if ((stepId === 'candidate-profile' || stepId === 'test-call') && demoCandidateId) {
      nextParams.candidate = demoCandidateId;
    }

    const currentStep = searchParams.get('step');
    const currentCandidate = searchParams.get('candidate') || '';
    if (currentStep === nextParams.step && currentCandidate === (nextParams.candidate || '')) {
      return;
    }

    setSearchParams(nextParams, { replace: true });
  }, [initialized, stepIndex, demoCandidateId, showComplete, searchParams, setSearchParams]);

  useEffect(() => {
    const fromQuery = searchParams.get('candidate') || '';
    const fromProfile = profile?.demo_candidate_id ? String(profile.demo_candidate_id) : '';

    if (fromQuery && fromQuery !== demoCandidateId) {
      if (!fromProfile && !demoCandidateId) return;
      if (fromProfile && fromQuery !== fromProfile && !demoCandidateId) return;
      setDemoCandidateId(fromQuery);
      return;
    }

    if (fromProfile && !demoCandidateId) {
      setDemoCandidateId(fromProfile);
    }
  }, [searchParams, demoCandidateId, profile?.demo_candidate_id]);

  useEffect(() => {
    if (!initialized) return;
    const fromQuery = searchParams.get('step');
    if (isDemoCompleteView(fromQuery)) {
      setShowComplete(true);
      return;
    }
    if (!isDemoStepId(fromQuery)) return;
    setShowComplete(false);
    const nextIndex = getDemoStepIndexById(fromQuery);
    setStepIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, [searchParams, initialized]);

  useEffect(() => {
    if (!demoCandidateId) return;

    let cancelled = false;

    const loadDemoCandidate = async () => {
      try {
        const row = await getCandidate(demoCandidateId);
        if (cancelled) return;
        if (!row) {
          setDemoCandidateId('');
          return;
        }
        const fullName = String(row.full_name || '').trim();
        setDemoCandidateName(fullName);
        setScrapeFormValues({
          first_name: fullName.split(/\s+/)[0] || fullName,
          linkedin_url: row.linkedin_url || '',
          email: row.email || '',
          phone: row.phone || '+',
        });
        setHasDemoPhone(hasCandidatePhone(row.phone));
      } catch (err) {
        console.error('Failed to load demo candidate for scrape form:', err);
        if (!cancelled) setDemoCandidateId('');
      }
    };

    loadDemoCandidate();
    return () => {
      cancelled = true;
    };
  }, [demoCandidateId, stepIndex]);

  useEffect(() => {
    const candidateId = demoCandidateId || profile?.demo_candidate_id;
    if (!candidateId) {
      setDemoCallCount(0);
      return undefined;
    }

    let cancelled = false;

    const loadDemoCallCount = async () => {
      const count = await getDemoCallsCount(candidateId);
      if (!cancelled) setDemoCallCount(count);
    };

    loadDemoCallCount();
    return () => {
      cancelled = true;
    };
  }, [demoCandidateId, profile?.demo_candidate_id]);

  if (profileLoading && !profile) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </MainLayout>
    );
  }

  if (profile && !isDemoIncomplete(profile) && !needsSubscriptionSelection(profile)) {
    return <Navigate to={getDefaultAuthenticatedPath(profile)} replace />;
  }

  const safeIndex = Math.min(Math.max(stepIndex, 0), DEMO_STEPS.length - 1);
  const currentStep = DEMO_STEPS[safeIndex];
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === DEMO_STEPS.length - 1;
  const hasDemoCandidate = Boolean(demoCandidateId || profile?.demo_candidate_id);

  const markStepCompleted = async (stepId) => {
    const nextCompleted = completedSet.has(stepId)
      ? completedSteps
      : [...completedSteps, stepId];

    const { error: updateError } = await updateProfile({
      demo_steps_completed: nextCompleted,
    });

    if (updateError) {
      throw new Error(updateError);
    }

    return nextCompleted;
  };

  const handleNext = async () => {
    if (!currentStep || saving || isLast) return;

    setSaving(true);
    setError('');

    try {
      await markStepCompleted(currentStep.id);
      setStepIndex((prev) => Math.min(prev + 1, DEMO_STEPS.length - 1));
    } catch (err) {
      console.error('Failed to save demo progress:', err);
      setError(err?.message || t('demo.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (saving) return;
    if (showComplete) {
      setShowComplete(false);
      setStepIndex(getDemoStepIndexById('candidate-profile'));
      return;
    }
    if (isFirst) return;
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleViewCandidate = () => {
    if (saving || !hasDemoCandidate) return;
    setStepIndex(getDemoStepIndexById('candidate-profile'));
  };

  const handleDeleteUnknownDemoCandidate = async () => {
    const candidateId = demoCandidateId || profile?.demo_candidate_id;
    if (!candidateId || saving) return;

    setSaving(true);
    setError('');

    try {
      await deleteCandidate(candidateId);

      const nextCompleted = completedSteps.filter((stepId) => (
        stepId !== 'test-scrape'
        && stepId !== 'candidate-profile'
        && stepId !== 'test-call'
        && stepId !== DEMO_TEST_CALL_SKIPPED_FLAG
      ));

      const { error: updateError } = await updateProfile({
        demo_candidate_id: null,
        demo_steps_completed: nextCompleted,
      });
      if (updateError) {
        throw new Error(updateError);
      }

      setDemoCandidateId('');
      setDemoCandidateName('');
      setShowDeleteConfirm(false);
      setScrapeFormValues({
        first_name: '',
        linkedin_url: '',
        email: '',
        phone: '+',
      });
      setHasDemoPhone(false);
      setIsDemoUnknown(false);
      setDemoCallCount(0);
      setShowCallWaiting(false);
      setStepIndex(getDemoStepIndexById('test-scrape'));
      setSearchParams({ step: 'test-scrape' }, { replace: true });
    } catch (err) {
      console.error('Failed to delete unknown demo candidate:', err);
      setError(err?.message || t('demo.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestCall = () => {
    if (saving || !hasDemoPhone || demoCallCount >= DEMO_CALL_LIMIT) return;
    setCallRequested(false);
    setCallError('');
    setShowTestCallModal(true);
  };

  const handleStartTestCall = async (voice) => {
    const agentId = voice?.vapiAgentId;
    const candidateId = demoCandidateId || profile?.demo_candidate_id;
    if (!agentId || !candidateId || isStartingCall || saving) return;
    if (demoCallCount >= DEMO_CALL_LIMIT) {
      setCallError(t('demo.callLimitReached', { limit: DEMO_CALL_LIMIT }));
      return;
    }

    setIsStartingCall(true);
    setCallError('');
    setError('');

    try {
      const callResponse = await startDemoCall({
        candidateId,
        agentId,
      });
      console.log('Demo call n8n response', callResponse);
      if (!callResponse.ok) {
        throw new Error(t('demo.callFailed'));
      }

      setDemoCallCount((prev) => Math.min(DEMO_CALL_LIMIT, prev + 1));
      setShowCallWaiting(true);
      setCallRequested(true);
      setIsStartingCall(false);
      getDemoCallsCount(candidateId).then((count) => {
        setDemoCallCount((prev) => Math.max(prev, count));
      }).catch(() => {});

      await new Promise((resolve) => {
        setTimeout(resolve, 3500);
      });
      setShowTestCallModal(false);
      setCallRequested(false);
    } catch (err) {
      console.error('Failed to start demo call:', err);
      setCallError(err?.message || t('demo.callFailed'));
    } finally {
      setIsStartingCall(false);
    }
  };

  const handleGoToComplete = async ({ skipped }) => {
    if (saving) return;
    setSaving(true);
    setError('');
    setShowTestCallModal(false);

    try {
      let nextCompleted = [...completedSteps];
      if (!nextCompleted.includes('candidate-profile')) {
        nextCompleted.push('candidate-profile');
      }

      if (skipped) {
        nextCompleted = nextCompleted.filter((stepId) => stepId !== 'test-call');
        if (!nextCompleted.includes(DEMO_TEST_CALL_SKIPPED_FLAG)) {
          nextCompleted.push(DEMO_TEST_CALL_SKIPPED_FLAG);
        }
      } else {
        nextCompleted = nextCompleted.filter((stepId) => stepId !== DEMO_TEST_CALL_SKIPPED_FLAG);
        if (!nextCompleted.includes('test-call')) {
          nextCompleted.push('test-call');
        }
      }

      const { error: updateError } = await updateProfile({
        demo_steps_completed: nextCompleted,
      });
      if (updateError) {
        throw new Error(updateError);
      }

      setStepIndex(getDemoStepIndexById('candidate-profile'));
      setShowComplete(true);
    } catch (err) {
      console.error('Failed to finish demo test call step:', err);
      setError(err?.message || t('demo.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const markDemoComplete = async () => {
    if (profile?.demo_completed_at) return;
    const { error: updateError } = await updateProfile({
      demo_completed_at: new Date().toISOString(),
    });
    if (updateError) {
      throw new Error(updateError);
    }
  };

  const handleChoosePlan = async () => {
    if (completeAction) return;
    setCompleteAction('plan');
    setError('');

    try {
      await markDemoComplete();
      navigate('/billing', { replace: true });
    } catch (err) {
      console.error('Failed to finish demo:', err);
      setError(err?.message || t('demo.saveFailed'));
      setCompleteAction(null);
    }
  };

  const handleStartTrial = async () => {
    if (completeAction) return;
    setCompleteAction('trial');
    setError('');

    try {
      await markDemoComplete();

      const { prices } = await getBillingPlans();
      const trial = mergeTrialWithStripePrice(prices);
      if (!trial?.price?.priceId) {
        throw new Error(t('billing.stripePriceNotConfiguredTrial'));
      }

      const { url } = await createCatalogCheckoutSession({
        productId: trial.id,
        priceId: trial.price.priceId,
      });
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start trial from demo:', err);
      setError(err?.message || t('billing.checkoutFailed'));
      setCompleteAction(null);
    }
  };

  const handleTestScrapeSubmit = async (candidateData) => {
    if (saving) return;
    setSaving(true);
    setError('');

    try {
      if (!user?.id) {
        throw new Error(t('demo.scrapeSignInRequired'));
      }

      const existing = await findCandidatesByLinkedinUrls(user.id, [candidateData.linkedin_url]);
      if (existing.has(candidateData.linkedin_url)) {
        throw new Error(t('demo.scrapeDuplicate'));
      }

      const maxCandidates = profile?.max_candidates || 50;
      const { total } = await getCandidatesStats(user.id);
      if (total >= maxCandidates) {
        throw new Error(
          getCandidateLimitTooltip(maxCandidates, planIsTopTier(profile?.subscription_plan), t),
        );
      }

      const payload = {
        ...toCandidateDbPayload(candidateData),
        is_demo: true,
      };
      const created = await createCandidate(user.id, payload);
      setDemoCandidateId(created.id);

      const nextCompleted = completedSet.has(currentStep.id)
        ? completedSteps
        : [...completedSteps, currentStep.id];
      const { error: updateError } = await updateProfile({
        demo_steps_completed: nextCompleted,
        demo_candidate_id: created.id,
      });
      if (updateError) {
        throw new Error(updateError);
      }

      setIsScraping(true);
      startDemoScraping(created.id)
        .then((scrapeResponse) => {
          console.log('Demo scrape n8n response', scrapeResponse);
        })
        .catch((scrapeError) => {
          console.error('Demo scrape n8n request failed', scrapeError);
        });

      await waitForDemoScrape();
      setIsScraping(false);

      setStepIndex((prev) => Math.min(prev + 1, DEMO_STEPS.length - 1));
    } catch (err) {
      console.error('Failed to start demo scrape:', err);
      setError(err?.message || t('demo.scrapeAddFailed'));
    } finally {
      setIsScraping(false);
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className={showComplete
        ? 'flex min-h-[calc(100vh-9rem)] items-center justify-center pb-24'
        : 'space-y-6 md:space-y-8 pb-24'
      }>
        {!showComplete && (
          <div>
            <h1 className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
              {t('demo.title')}
              <span className="pointer-events-none inline-flex select-none items-center rounded-full border-2 border-primary bg-primary/15 px-3.5 py-1 text-sm md:text-base font-bold uppercase tracking-[0.14em] text-primary">
                {t('demo.badge')}
              </span>
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              {t('demo.subtitle')}
            </p>
          </div>
        )}

        {!showComplete && (
          <div className="rounded-lg border border-border bg-card px-4 py-4 md:px-6 md:py-5 shadow-elevation-sm space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-foreground">
                  {t('demo.progressTitle')}
                </p>
                <p className="text-sm font-medium text-primary">
                  {t('demo.progress', { current: safeIndex + 1, total: DEMO_STEPS.length })}
                </p>
              </div>
              <div
                className="flex gap-1.5"
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={DEMO_STEPS.length}
                aria-valuenow={safeIndex + 1}
                aria-label={t('demo.progress', { current: safeIndex + 1, total: DEMO_STEPS.length })}
              >
                {DEMO_STEPS.map((step, index) => {
                  const reached = index <= safeIndex;
                  const isFilled = step.id === 'test-call'
                    ? reached && completedSet.has('test-call')
                    : reached;
                  return (
                    <div
                      key={step.id}
                      className={`h-2.5 flex-1 rounded-full transition-colors duration-300 ${
                        isFilled ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <h2 className="text-base md:text-lg font-semibold text-foreground">
                {t(currentStep.titleKey)}
              </h2>
              <p className="text-xs md:text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                {t(currentStep.bodyKey)}
              </p>
              {currentStep.id === 'test-scrape' && (
                <div className="flex gap-2.5 mt-2">
                  <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-foreground leading-relaxed">
                    {t('demo.steps.test-scrape.notice')}
                  </p>
                </div>
              )}
              {(currentStep.id === 'candidate-profile' || currentStep.id === 'test-call') && !hasDemoPhone && (
                <div className="flex gap-2.5 mt-2">
                  <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-foreground leading-relaxed">
                    {t('demo.steps.candidate-profile.noPhone')}
                  </p>
                </div>
              )}
              {(currentStep.id === 'candidate-profile' || currentStep.id === 'test-call') && showCallWaiting && (
                <div className="flex gap-2.5 mt-2">
                  <Icon name="Phone" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-foreground leading-relaxed">
                    {t('demo.callWaiting')}
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        <div className={showComplete ? 'w-full' : 'min-w-0'}>
          {showComplete ? (
            <DemoComplete
              onStartTrial={handleStartTrial}
              onChoosePlan={handleChoosePlan}
              startingTrial={completeAction === 'trial'}
              choosingPlan={completeAction === 'plan'}
              error={error}
            />
          ) : currentStep.id === 'welcome' ? (
            <DemoWelcomeVideo />
          ) : currentStep.id === 'test-scrape' ? (
            <DemoTestScrapeForm
              formId={TEST_SCRAPE_FORM_ID}
              onValidSubmit={handleTestScrapeSubmit}
              disabled={hasDemoCandidate || isScraping}
              initialValues={scrapeFormValues}
            />
          ) : currentStep.id === 'candidate-profile' || currentStep.id === 'test-call' ? (
            <DemoCandidateProfile
              candidateId={demoCandidateId}
              onPhoneChange={setHasDemoPhone}
              onUnknownChange={setIsDemoUnknown}
              onDeleteUnknown={handleDeleteUnknownDemoCandidate}
              callsRefreshKey={demoCallCount}
            />
          ) : (
            <DemoStepPreview stepId={currentStep.id} />
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card p-1 lg:left-[240px]">
        <div className="flex h-16 items-center justify-between gap-3 px-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={(!showComplete && isFirst) || saving || isScraping}
            iconName="ArrowLeft"
          >
            {t('demo.back')}
          </Button>
          {showComplete ? (
            <div />
          ) : currentStep.id === 'candidate-profile' || currentStep.id === 'test-call' ? (
            <div className="flex items-center gap-2">
              {isDemoUnknown && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={saving}
                  iconName="Trash2"
                >
                  {t('common.delete')}
                </Button>
              )}
              {demoCallCount === 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleGoToComplete({ skipped: true })}
                  disabled={saving}
                >
                  {t('demo.finish')}
                </Button>
              )}
              {isDemoUnknown ? null : hasDemoPhone && demoCallCount < DEMO_CALL_LIMIT ? (
                <Button
                  type="button"
                  onClick={handleTestCall}
                  disabled={saving}
                  iconName="Phone"
                  iconPosition="right"
                >
                  {t('demo.startTestCall')}
                </Button>
              ) : (
                <Tooltip
                  content={
                    !hasDemoPhone
                      ? t('demo.steps.candidate-profile.noPhone')
                      : t('demo.callLimitReached', { limit: DEMO_CALL_LIMIT })
                  }
                  align="end"
                  size="md"
                >
                  <span className="inline-flex cursor-not-allowed">
                    <Button
                      type="button"
                      disabled
                      iconName="Phone"
                      iconPosition="right"
                    >
                      {t('demo.startTestCall')}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {demoCallCount > 0 && (
                <Button
                  type="button"
                  onClick={() => handleGoToComplete({ skipped: false })}
                  disabled={saving}
                >
                  {t('demo.finish')}
                </Button>
              )}
            </div>
          ) : currentStep.id === 'test-scrape' && isScraping ? (
            <Button type="button" disabled>
              {t('demo.scrapingWaitButton')}
            </Button>
          ) : currentStep.id === 'test-scrape' && hasDemoCandidate ? (
            <Button
              type="button"
              onClick={handleViewCandidate}
              disabled={saving}
              iconName="User"
              iconPosition="right"
            >
              {t('demo.viewCandidate')}
            </Button>
          ) : currentStep.id === 'test-scrape' ? (
            <Button
              type="submit"
              form={TEST_SCRAPE_FORM_ID}
              loading={saving}
              disabled={saving}
              iconName={saving ? null : 'Play'}
              iconPosition="right"
            >
              {t('demo.startTestScrape')}
            </Button>
          ) : !isLast ? (
            <Button
              type="button"
              onClick={handleNext}
              loading={saving}
              disabled={saving}
              iconName="ArrowRight"
              iconPosition="right"
            >
              {t('demo.next')}
            </Button>
          ) : (
            <div />
          )}
        </div>
      </div>

      {isScraping && <DemoScrapeOverlay />}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDeleteUnknownDemoCandidate();
        }}
        title={t('candidates.deleteCandidateTitle')}
        message={t('candidates.deleteCandidateMessage', { name: demoCandidateName })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
      />

      <DemoTestCallModal
        isOpen={showTestCallModal}
        onClose={() => {
          if (isStartingCall) return;
          setShowTestCallModal(false);
          setCallError('');
          setCallRequested(false);
        }}
        preferredLocale={profile?.preferred_locale}
        onStartCall={handleStartTestCall}
        starting={isStartingCall}
        waiting={callRequested}
        error={callError}
        usedCalls={demoCallCount}
        callLimit={DEMO_CALL_LIMIT}
      />
    </MainLayout>
  );
};

export default DemoPage;
